import React, { useState, useRef, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Check, 
  ChevronDown, 
  Search, 
  X, 
  MapPin, 
  CheckSquare, 
  Square, 
  MinusSquare,
  Sparkles,
  Layers
} from 'lucide-react';

export interface StateOption {
  name: string;
  code: string;
}

export interface StateMultiSelectDropdownProps {
  states: StateOption[];
  selectedCodes: string[];
  onChange: (selectedCodes: string[]) => void;
  disabled?: boolean;
  className?: string;
  id?: string;
  placeholder?: string;
}

export const StateMultiSelectDropdown: React.FC<StateMultiSelectDropdownProps> = ({
  states,
  selectedCodes = [],
  onChange,
  disabled = false,
  className = '',
  id = 'state-multi-select',
  placeholder = 'Select Target States...'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const selectAllCheckboxRef = useRef<HTMLInputElement>(null);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Auto-focus search input when opened
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 60);
      return () => clearTimeout(timer);
    } else {
      setSearchQuery('');
    }
  }, [isOpen]);

  // Autocomplete filtering based on state name or 2-letter abbreviation
  const filteredStates = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return states;
    return states.filter(s => 
      s.name.toLowerCase().includes(query) || 
      s.code.toLowerCase().includes(query)
    );
  }, [states, searchQuery]);

  const totalStates = states.length;
  const selectedCount = selectedCodes.length;
  const allSelected = totalStates > 0 && selectedCount === totalStates;
  const noneSelected = selectedCount === 0;
  const isIndeterminate = selectedCount > 0 && selectedCount < totalStates;

  // Sync indeterminate property on master checkbox
  useEffect(() => {
    if (selectAllCheckboxRef.current) {
      selectAllCheckboxRef.current.indeterminate = isIndeterminate;
    }
  }, [isIndeterminate, selectedCodes, states]);

  // Master Select All / Unselect All toggle
  const handleToggleSelectAll = () => {
    if (allSelected) {
      onChange([]);
    } else {
      onChange(states.map(s => s.code));
    }
  };

  // Toggle single state selection
  const handleToggleState = (code: string) => {
    if (selectedCodes.includes(code)) {
      onChange(selectedCodes.filter(c => c !== code));
    } else {
      onChange([...selectedCodes, code]);
    }
  };

  // Filter-specific batch actions when searching
  const isFilteredSearch = searchQuery.trim().length > 0;
  const filteredCodes = useMemo(() => filteredStates.map(s => s.code), [filteredStates]);
  const allFilteredSelected = filteredCodes.length > 0 && filteredCodes.every(c => selectedCodes.includes(c));

  const handleToggleFiltered = () => {
    if (allFilteredSelected) {
      // Unselect only the filtered ones
      onChange(selectedCodes.filter(c => !filteredCodes.includes(c)));
    } else {
      // Add all filtered ones to existing selection
      const merged = Array.from(new Set([...selectedCodes, ...filteredCodes]));
      onChange(merged);
    }
  };

  // Quick preset: Top 5 procurement states
  const handleSelectHotStates = () => {
    const hotCodes = ['TX', 'CA', 'FL', 'NY', 'IL'];
    onChange(hotCodes.filter(c => states.some(s => s.code === c)));
  };

  // Format trigger button label
  const getTriggerLabel = () => {
    if (noneSelected) {
      return (
        <span className="text-slate-400 font-normal">
          {placeholder}
        </span>
      );
    }

    if (allSelected) {
      return (
        <span className="flex items-center gap-1.5 text-indigo-700 font-bold">
          <span>All 50 States Selected</span>
          <span className="px-1.5 py-0.2 bg-indigo-100 text-indigo-800 rounded-md text-[10px] font-mono">
            50
          </span>
        </span>
      );
    }

    if (selectedCount === 1) {
      const match = states.find(s => s.code === selectedCodes[0]);
      return (
        <span className="text-slate-900 font-semibold truncate">
          {match ? `${match.name} (${match.code})` : selectedCodes[0]}
        </span>
      );
    }

    if (selectedCount <= 3) {
      return (
        <span className="flex items-center gap-1 text-slate-900 font-semibold truncate">
          <span>{selectedCodes.join(', ')}</span>
          <span className="px-1.5 py-0.2 bg-slate-200 text-slate-700 rounded-md text-[10px] font-mono ml-1">
            {selectedCount}
          </span>
        </span>
      );
    }

    // > 3 states
    const previewCodes = selectedCodes.slice(0, 3).join(', ');
    const remaining = selectedCount - 3;
    return (
      <span className="flex items-center gap-1 text-slate-900 font-semibold truncate">
        <span>{previewCodes}</span>
        <span className="px-1.5 py-0.2 bg-indigo-100 text-indigo-800 rounded-md text-[10px] font-mono ml-1">
          +{remaining} ({selectedCount} States)
        </span>
      </span>
    );
  };

  return (
    <div ref={containerRef} className={`relative inline-block text-left w-full ${className}`}>
      {/* Combo Box Button */}
      <button
        type="button"
        id={id}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full flex items-center justify-between gap-2 px-3 py-2 border rounded-xl text-xs transition-all outline-none text-left cursor-pointer ${
          disabled 
            ? 'bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed'
            : isOpen 
              ? 'bg-white border-indigo-500 ring-2 ring-indigo-500/20 shadow-xs' 
              : 'bg-slate-50 hover:bg-white border-slate-200 focus:border-indigo-500 hover:border-slate-300'
        }`}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <MapPin className={`w-3.5 h-3.5 shrink-0 ${selectedCount > 0 ? 'text-indigo-600' : 'text-slate-400'}`} />
          <div className="truncate text-xs">
            {getTriggerLabel()}
          </div>
        </div>

        <div className="flex items-center gap-1 shrink-0 ml-1">
          {selectedCount > 0 && !disabled && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                onChange([]);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.stopPropagation();
                  onChange([]);
                }
              }}
              className="p-1 rounded-md hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors"
              title="Clear state selection"
            >
              <X className="w-3 h-3" />
            </span>
          )}
          <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform duration-200 ${isOpen ? 'rotate-180 text-indigo-600' : ''}`} />
        </div>
      </button>

      {/* Dropdown Popover */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 4, scale: 0.98 }}
            transition={{ duration: 0.15 }}
            className="absolute z-50 mt-1.5 w-full min-w-[290px] max-w-sm sm:max-w-md bg-white border border-slate-200 rounded-2xl shadow-xl focus:outline-none overflow-hidden"
          >
            {/* 1. Autocomplete Search Textbox */}
            <div className="p-2.5 border-b border-slate-100 bg-slate-50/80">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search state name or 2-letter code (e.g. TX, California)..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-8 pr-8 py-1.5 bg-white border border-slate-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 rounded-xl text-xs outline-none transition-all placeholder:text-slate-400 font-medium"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>

              {/* Status & Match Indicator */}
              <div className="flex items-center justify-between mt-2 px-1 text-[11px] text-slate-500">
                <span>
                  {isFilteredSearch ? (
                    <span>Matches: <strong className="text-slate-800">{filteredStates.length}</strong> of {totalStates} states</span>
                  ) : (
                    <span>Target Portals: <strong className="text-slate-800">{totalStates}</strong> US States</span>
                  )}
                </span>
                <span className="font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-md text-[10px]">
                  {selectedCount} Selected
                </span>
              </div>
            </div>

            {/* 2. Select All / Unselect All Master Checkbox Toolbar */}
            <div className="px-3 py-2 border-b border-slate-100 bg-white flex items-center justify-between gap-2">
              <label className="flex items-center gap-2 text-xs font-bold text-slate-800 cursor-pointer select-none">
                <input
                  ref={selectAllCheckboxRef}
                  type="checkbox"
                  checked={allSelected}
                  onChange={handleToggleSelectAll}
                  className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer accent-indigo-600"
                />
                <span className="flex items-center gap-1.5">
                  <span>{allSelected ? 'Unselect All (50 States)' : 'Select All (50 States)'}</span>
                </span>
              </label>

              <div className="flex items-center gap-1">
                {isFilteredSearch && filteredStates.length > 0 && (
                  <button
                    type="button"
                    onClick={handleToggleFiltered}
                    className="px-2 py-1 text-[10px] font-bold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors cursor-pointer"
                  >
                    {allFilteredSelected ? 'Deselect Filtered' : `Select Filtered (${filteredStates.length})`}
                  </button>
                )}
                {!isFilteredSearch && (
                  <button
                    type="button"
                    onClick={handleSelectHotStates}
                    className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 hover:text-slate-800 rounded-lg transition-colors cursor-pointer"
                    title="Quickly select top 5 procurement states: TX, CA, FL, NY, IL"
                  >
                    <Sparkles className="w-2.5 h-2.5 text-amber-500" />
                    <span>Top 5 States</span>
                  </button>
                )}
              </div>
            </div>

            {/* 3. Scrollable List of States with Multi-Select Checkboxes */}
            <div className="max-h-60 overflow-y-auto divide-y divide-slate-100/80 p-1">
              {filteredStates.length === 0 ? (
                <div className="py-8 px-4 text-center">
                  <p className="text-xs font-semibold text-slate-600">No states match "{searchQuery}"</p>
                  <p className="text-[11px] text-slate-400 mt-1">Try searching by full state name or 2-letter postal code</p>
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="mt-3 px-3 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-lg transition-colors"
                  >
                    Clear Filter
                  </button>
                </div>
              ) : (
                filteredStates.map((state) => {
                  const isChecked = selectedCodes.includes(state.code);
                  return (
                    <label
                      key={state.code}
                      className={`flex items-center justify-between px-3 py-2 text-xs rounded-xl cursor-pointer transition-colors ${
                        isChecked 
                          ? 'bg-indigo-50/70 text-indigo-950 font-semibold' 
                          : 'hover:bg-slate-50 text-slate-700'
                      }`}
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => handleToggleState(state.code)}
                          className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer accent-indigo-600 shrink-0"
                        />
                        <span className="truncate select-none">
                          {state.name}
                        </span>
                      </div>

                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-mono font-bold shrink-0 ml-2 ${
                        isChecked 
                          ? 'bg-indigo-200/70 text-indigo-800' 
                          : 'bg-slate-100 text-slate-500'
                      }`}>
                        {state.code}
                      </span>
                    </label>
                  );
                })
              )}
            </div>

            {/* 4. Footer Bar */}
            <div className="p-2.5 border-t border-slate-100 bg-slate-50 flex items-center justify-between gap-2">
              <div className="text-[11px] text-slate-500 truncate">
                {selectedCount === 0 ? (
                  <span className="text-amber-600 font-medium">⚠️ Select at least 1 state to harvest</span>
                ) : (
                  <span>
                    Ready to audit <strong className="text-slate-900">{selectedCount}</strong> official portal{selectedCount > 1 ? 's' : ''}
                  </span>
                )}
              </div>

              <div className="flex items-center gap-1.5 shrink-0">
                {selectedCount > 0 && (
                  <button
                    type="button"
                    onClick={() => onChange([])}
                    className="px-2.5 py-1 text-xs font-semibold text-slate-500 hover:text-slate-800 hover:bg-slate-200/70 rounded-lg transition-colors cursor-pointer"
                  >
                    Clear
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="px-3 py-1 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-lg shadow-2xs transition-colors cursor-pointer"
                >
                  Done
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
